import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getTasksApi } from "../../../../services/Admin/calendar";
import EditTask from "../Fields";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import Dialog from "@mui/material/Dialog";
import {
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from "@mui/material";
import LoadingProgress from "../../../../components/LoadingProgress";
import AdvancedSearchButton from "./AdvancedSearchButton";
import Card from "../../../../components/Admin/Card";

export default function Management() {
    const [tasks, setTasks] = useState([]);
    const [idTask, setIdTask] = useState(null);
    const [dateTask, setDateTask] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [selectedWorker, setSelectedWorker] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [workers, setWorkers] = useState([]);
    const [currentView, setCurrentView] = useState("dayGridMonth");
    const [weekRange, setWeekRange] = useState({ start: null, end: null });

    // --- Carga tareas del mes actual ---
    const getTask = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await getTasksApi("calendar", year, month);
            const temp = resp.data.data.map((doc) => ({
                ...doc,
                id: doc.id,
                color: `#${doc.idWorker?.color || "2196f3"}`,
                title: `${doc.idWorker?.name} ${doc.idWorker?.surnames}`,
                date: doc.dateTask * 1000,
                start: doc.startHour * 1000,
                end: doc.endHour * 1000,
                workerId: doc.idWorker?._id || doc.idWorker?.id,
            }));
            setTasks(temp);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    // --- Cierra el diálogo y recarga ---
    const handleClose = () => {
        setOpen(false);
        getTask();
    };

    const clickDay = (arg) => {
        setIdTask(null);
        setDateTask(arg);
        setOpen(true);
    };

    const clickTask = (id) => {
        openTask(id)
    }

    const openTask = (id) => {
        setIdTask(id)
        setDateTask(null)
        setOpen(true)
    }

    // --- Efectos de carga ---
    useEffect(() => {
        getTask();
    }, [year, month, getTask]);

    // --- Trabajadores únicos de todas las tareas del año ---
    const getWorkers = () => {
        if (!tasks.length) return [];

        const map = new Map();

        tasks.forEach((task) => {
            if (task.idWorker) {
                const workerId = task.idWorker.id || task.idWorker._id;
                // Si el treballador no està al mapa, s’hi afegeix amb comptador inicial 1
                if (!map.has(workerId)) {
                    map.set(workerId, {
                        ...task.idWorker,
                        totalTasks: 1, // ✅ Primera tasca trobada
                    });
                } else {
                    // Si ja existeix, s’incrementa el comptador
                    const existing = map.get(workerId);
                    map.set(workerId, {
                        ...existing,
                        totalTasks: existing.totalTasks + 1,
                    });
                }
            }
        });

        // Converteix el mapa a array i actualitza l’estat
        setWorkers(Array.from(map.values()));
    };

    useEffect(() => {
        getWorkers();
    }, [tasks]);

    // --- Filtra las tareas del mes según el trabajador ---
    const filteredTasks = useMemo(() => {
        return selectedWorker
            ? tasks?.filter((task) => task.workerId === selectedWorker)
            : tasks;
    }, [tasks, selectedWorker]);

    // --- Nombre del mes y año para el título ---
    const monthNames = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    const calendarTitle =
        `${monthNames[month - 1]} ${year}` +
        (
            filteredTasks?.length !== tasks?.length
                ? ` (${filteredTasks?.length || 0} / ${tasks?.length || 0} tareas)`
                : ` (${tasks?.length || 0} tareas)`
        );

    return (
        <>
            <div style={{ position: "relative", marginBottom: "-80px", background: "#fff", padding: 20, borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", marginTop: 0 }}>
                <Dialog fullScreen open={open} onClose={handleClose}>
                    <EditTask
                        handleClose={handleClose}
                        openTask={openTask}
                        idTask={idTask}
                        dateTask={dateTask}
                        parent="calendario"
                    />
                </Dialog>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gridGap: 16 }}>
                    {/* Selector de trabajador */}
                    <FormControl
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{
                            mb: 1,
                            '& .MuiInputBase-root': {
                                borderRadius: 2,
                                backgroundColor: '#fafafa',
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: 14,
                            },
                        }}
                    >
                        <InputLabel id="worker-select-label">Trabajador</InputLabel>
                        <Select
                            labelId="worker-select-label"
                            value={selectedWorker}
                            label="Trabajador"
                            onChange={(e) => setSelectedWorker(e.target.value)}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: 240,
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    },
                                },
                            }}
                        >
                            <MenuItem
                                value=""
                                style={{ borderLeft: `4px solid transparent`, paddingLeft: 19, fontSize: 14, py: 0.5, px: 1.5 }}
                            >
                                Todos
                            </MenuItem>
                            {workers.map((worker) => (
                                <MenuItem
                                    key={worker._id || worker.id}
                                    value={worker._id || worker.id}
                                    sx={{
                                        fontSize: 14,
                                        py: 0.5,
                                        px: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    {/* <span
                                        style={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            backgroundColor: `#${worker.color}`,
                                            flexShrink: 0,
                                        }}
                                    /> */}
                                    <span style={{ borderLeft: `4px solid #${worker.color}`, paddingLeft: 8 }}>
                                        {worker.name} {worker.surnames}
                                        <Typography
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                fontSize: 12,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            ({worker.totalTasks})
                                        </Typography>
                                    </span>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <AdvancedSearchButton currentView={currentView} weekRange={weekRange} />
                </div>
                {/* 🔹 FullCalendar container */}
                {loading && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'rgba(255,255,255,0.6)',
                            backdropFilter: 'blur(2px)',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <LoadingProgress />
                    </div>
                )}

                {/* Fuerza los números de los días del mes y los nombres de los días a negro, y el fondo de las celdas de los días de la semana a gris */}
                <style>
                    {`
                        .fc .fc-daygrid-day-number {
                            color: #111 !important;
                        }
                        .fc .fc-col-header-cell-cushion {
                            color: #111 !important;
                        }
                        .fc .fc-col-header-cell {
                            background: #f5f5f5 !important;
                        }
                    `}
                </style>
                <FullCalendar
                    firstDay={1}
                    datesSet={(arg) => {
                        const currentMonth = arg.view.currentStart.getMonth() + 1;
                        const currentYear = arg.view.currentStart.getFullYear();
                        setYear(currentYear);
                        setMonth(currentMonth);

                        setWeekRange({
                            start: arg.view.activeStart,
                            end: arg.view.activeEnd,
                        });
                    }}
                    viewDidMount={(arg) => setCurrentView(arg.view.type)}
                    viewWillUnmount={() => setCurrentView("")}
                    plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        listPlugin,
                        interactionPlugin,
                    ]}
                    initialView="dayGridMonth"
                    locale="es"
                    allDayText="Horas"
                    titleFormat={() => {
                        // Devuelve string vacío, el título lo renderizamos manualmente
                        return '';
                    }}
                    customButtons={{
                        calendarTitle: {
                            text: calendarTitle
                        }
                    }}
                    headerToolbar={{
                        left: "prev,next today",
                        center: "calendarTitle",
                        right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
                    }}
                    buttonIcons={false}
                    navLinks={true}
                    editable={true}
                    buttonText={{
                        today: "Hoy",
                        month: "Mes",
                        week: "Semana",
                        day: "Día",
                        list: "Lista",
                        prev: "<",
                        next: ">",
                    }}
                    displayEventTime={true}
                    displayEventEnd={true}
                    eventTimeFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }}
                    height="80vh"
                    dateClick={(arg) => clickDay(arg.dateStr)}
                    eventClick={(clickInfo) => {
                        clickTask(clickInfo?.event.id);
                    }}
                    events={filteredTasks}
                    eventContent={(eventInfo) => {
                        // Format start and end times as HH:mm
                        const formatTime = (date) => {
                            if (!date) return '';
                            const d = new Date(date);
                            return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false });
                        };
                        return (
                            <div
                                style={{
                                    fontSize: 12,
                                    padding: '2px 4px',
                                    borderRadius: 4,
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <span
                                    style={{
                                        borderLeft: `4px solid ${eventInfo.event.backgroundColor}`,
                                        paddingLeft: 4,
                                        fontWeight: 600,
                                        fontSize: 12,
                                        color: '#222',
                                        letterSpacing: 1,
                                    }}
                                >
                                    {formatTime(eventInfo.event.start)} - {formatTime(eventInfo.event.end)}
                                </span>
                                <span
                                    style={{
                                        color: '#222',
                                        fontWeight: 500,
                                        background: 'none', // <-- sin fondo
                                        borderRadius: 0,
                                    }}
                                >
                                    {eventInfo.event.title}
                                </span>
                            </div>
                        );
                    }}
                />
            </div>
        </>
    );
}
